import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  PencilAltIcon,
  TrashIcon,
  EyeIcon,
  CashIcon,
  CreditCardIcon,
  ExclamationCircleIcon,
  ClockIcon,
  UserIcon,
  CalendarIcon,
  OfficeBuildingIcon,
  DocumentTextIcon,
  SearchIcon,
} from "@heroicons/react/solid";
import Cookies from "universal-cookie";
import { ROLE } from "../../config";
import { Button, DateInput } from "../../components/ui";
import { formatDateWithHyphens } from "../../utility";
import BreadCrumbs from "../../components/ui/BreadCrumbs";
import RoundedContainer from "../../components/containers/RoundedContainer";
import InlineLoader from "../../components/loader/InlineLoader";
import Pagination from "../../components/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";
import { usePettyCashFundRequestDetail } from "../../hooks/petty_cash/usePettyCashFundRequestDetail";
import { usePettyCashSettings } from "../../hooks/petty_cash/usePettyCashSettings";
import FundRequestLifecycleTimeline from "../../components/petty_cash/FundRequestLifecycleTimeline";
import FundRequestApprovalModal from "../../components/petty_cash/FundRequestApprovalModal";
import FundRequestMarkAsPaidModal from "../../components/petty_cash/FundRequestMarkAsPaidModal";
import FundRequestForm from "../../components/petty_cash/FundRequestForm";
import DeleteConfirmationModal from "../../components/ui/DeleteConfirmationModal";
import PettyCashDetailModal from "../../components/petty_cash/PettyCashDetailModal";

const cookies = new Cookies();

const statusStyles = {
  pending: "status-pending",
  approved: "status-approved",
  rejected: "status-rejected",
  partial_approval: "bg-yellow-100 text-yellow-800 px-2.5 py-0.5 rounded-full text-xs font-bold",
};

const statusLabels = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  partial_approval: "Partial Approval",
};

const paymentStatusStyles = {
  paid: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  payment_pending: "bg-amber-100 text-amber-800 border border-amber-200",
  not_applicable: "bg-gray-100 text-gray-600 border border-gray-200",
};

const paymentStatusLabels = {
  paid: "Paid",
  payment_pending: "Payment Pending",
  not_applicable: "N/A",
};

const stageLabels = {
  REQUEST_SUBMITTED: "Submitted",
  PENDING_APPROVAL: "Pending Approval",
  PARTIAL_APPROVAL: "Partially Approved",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  PAYMENT_PENDING: "Payment Pending",
  PAID: "Paid",
};

function PettyCashFundRequestDetail() {
  const { id: requestId } = useParams();
  const navigate = useNavigate();
  const role = cookies.get(ROLE);
  const isAdmin = role === "COMPANY_ADMIN";

  const [approvalModal, setApprovalModal] = useState(false);
  const [paidModal, setPaidModal] = useState(false);
  const [formModal, setFormModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const {
    lifecycleQuery,
    singleRequestQuery,
    transactionListing,
    activeTxnQuery,
    selectedTxnId,
    setSelectedTxnId,
    txnFilters,
    searchQuery,
    txnLimit,
    handleSearchChange,
    handleFilterChange,
    handleViewTxnDetails,
    handleTxnPageClick,
    approveRequest,
    isApproving,
    markAsPaid,
    isMarkingAsPaid,
    deleteRequest,
    isDeleting,
  } = usePettyCashFundRequestDetail({
    requestId,
    onApprovalSuccess: () => {
      setApprovalModal(false);
    },
    onMarkAsPaidSuccess: () => {
      setPaidModal(false);
    },
    onDeleteSuccess: () => {
      navigate("/petty-cash/fund-requests");
    },
  });

  const { settingsData } = usePettyCashSettings();
  const settings = settingsData.data?.data;
  const priorNoDays = settings && !Array.isArray(settings) ? parseInt(settings.prior_no_days, 10) : 0;
  const isSettingsLoading = settingsData.isLoading;

  const lifecycleData = lifecycleQuery.data?.data || {};
  const extendedDetails = singleRequestQuery.data?.data || {};

  const request = {
    ...extendedDetails,
    ...lifecycleData,
  };

  const transactions = transactionListing.data?.data?.transaction_data || [];
  const totalTxnCount = transactionListing.data?.total_count || 0;
  const activeTxn = activeTxnQuery.data?.data?.[0];

  const isLoading = lifecycleQuery.isLoading && singleRequestQuery.isLoading;

  const handleApprove = (payload) => {
    approveRequest(payload);
  };

  return (
    <div className="py-6 px-4 md:px-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Top Navigation & Breadcrumbs */}
        <BreadCrumbs
          crumbs={[
            { label: "Dashboard", link: "/" },
            { label: "Fund Requests", link: "/petty-cash/fund-requests" },
            { label: `Req #${requestId}`, link: "" },
          ]}
        />

        {/* Page Header */}
        <div className="bg-white rounded-2xl border border-gray-150 p-5 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/petty-cash/fund-requests")}
              className="p-2.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors flex-shrink-0"
              title="Back to Fund Requests"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center space-x-3 flex-wrap gap-y-1">
                <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
                  {request.title || `Fund Request #${requestId}`}
                </h1>
                {request.approval_status && (
                  <span className={statusStyles[request.approval_status] || "status-pending"}>
                    {statusLabels[request.approval_status] || request.approval_status}
                  </span>
                )}
                {request.payment_status && request.payment_status !== "not_applicable" && (
                  <span
                    className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${
                      paymentStatusStyles[request.payment_status] || "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {paymentStatusLabels[request.payment_status] || request.payment_status}
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-sm mt-1">
                Requested by <span className="font-semibold text-gray-800">{request.employee_name || "Employee"}</span>
                {request.employee_code ? ` (${request.employee_code})` : ""}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 self-end md:self-auto">
            {request.approval_status === "pending" && isAdmin && (
              <Button
                onClick={() => setApprovalModal(true)}
                className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700"
              >
                <CheckCircleIcon className="w-5 h-5" />
                <span>Review Request</span>
              </Button>
            )}

            {isAdmin &&
              (request.approval_status === "approved" || request.approval_status === "partial_approval") &&
              request.payment_status !== "paid" && (
                <Button
                  onClick={() => setPaidModal(true)}
                  className="inline-flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700"
                >
                  <CreditCardIcon className="w-5 h-5" />
                  <span>Mark as Paid</span>
                </Button>
              )}

            {request.approval_status === "pending" && !isAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setFormModal(true)}
                  className="inline-flex items-center space-x-2"
                >
                  <PencilAltIcon className="w-4 h-4 text-blue-600" />
                  <span>Edit</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDeleteModal(true)}
                  className="inline-flex items-center space-x-2 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <TrashIcon className="w-4 h-4 text-red-600" />
                  <span>Delete</span>
                </Button>
              </>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20 w-full">
            <InlineLoader className="w-8 h-8 text-primary" />
          </div>
        ) : (
          <>
            {/* KPI Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {/* Requested Amount */}
              <div className="bg-white border border-gray-150 rounded-2xl p-4 shadow-xs flex flex-col justify-between space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Requested
                  </span>
                  <div className="p-2 rounded-xl bg-blue-50 text-blue-600">
                    <CashIcon className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {request.currency_type_symbol || request.currency_symbol || "₹"}
                  {request.requested_amount || 0}
                </div>
              </div>

              {/* Approved Amount */}
              <div className="bg-white border border-gray-150 rounded-2xl p-4 shadow-xs flex flex-col justify-between space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Approved
                  </span>
                  <div className="p-2 rounded-xl bg-green-50 text-green-600">
                    <CheckCircleIcon className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-2xl font-black text-green-600">
                  ₹{request.approved_amount ?? 0}
                </div>
              </div>

              {/* Rejected Amount */}
              <div className="bg-white border border-gray-150 rounded-2xl p-4 shadow-xs flex flex-col justify-between space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Rejected
                  </span>
                  <div className="p-2 rounded-xl bg-red-50 text-red-600">
                    <ExclamationCircleIcon className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-2xl font-black text-red-600">
                  ₹{request.rejected_amount ?? 0}
                </div>
              </div>

              {/* Paid Amount */}
              <div className="bg-white border border-gray-150 rounded-2xl p-4 shadow-xs flex flex-col justify-between space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Paid
                  </span>
                  <div className="p-2 rounded-xl bg-purple-50 text-purple-600">
                    <CreditCardIcon className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-2xl font-black text-purple-600">
                  ₹{request.paid_amount ?? 0}
                </div>
              </div>

              {/* Payment Due */}
              <div className="bg-white border border-gray-150 rounded-2xl p-4 shadow-xs flex flex-col justify-between space-y-2 col-span-2 sm:col-span-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Payment Due
                  </span>
                  <div className="p-2 rounded-xl bg-amber-50 text-amber-600">
                    <ClockIcon className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-2xl font-black text-amber-600">
                  ₹{request.payment_due ?? 0}
                </div>
              </div>
            </div>

            {/* Lifecycle & Request Info Section */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              {/* Left: Request Information (4 cols) */}
              <div className="lg:col-span-4">
                <RoundedContainer className="p-5 border border-gray-150 shadow-xs space-y-4">
                  <h3 className="text-base font-bold text-gray-900 pb-3 border-b border-gray-100 flex items-center gap-2">
                    <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                    Request Information
                  </h3>

                  <div className="space-y-4 text-sm">
                    <div className="flex items-start space-x-3 pb-3 border-b border-gray-100">
                      <UserIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-xs text-gray-400 font-bold uppercase block">
                          Employee
                        </span>
                        <span className="font-semibold text-gray-800 block truncate">
                          {request.employee_name || "-"}
                        </span>
                        {request.employee_code && (
                          <span className="text-gray-500 text-xs block">
                            Code: {request.employee_code}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 pb-3 border-b border-gray-100">
                      <CalendarIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-xs text-gray-400 font-bold uppercase block">
                          Expense Period
                        </span>
                        <span className="font-semibold text-gray-800">
                          {request.from_date || "-"} &rarr; {request.to_date || "-"}
                        </span>
                      </div>
                    </div>

                    {request.entity_name && (
                      <div className="flex items-start space-x-3 pb-3 border-b border-gray-100">
                        <OfficeBuildingIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="text-xs text-gray-400 font-bold uppercase block">
                            Entity / Merchant
                          </span>
                          <span className="font-semibold text-gray-800 block truncate">
                            {request.entity_name}
                          </span>
                        </div>
                      </div>
                    )}

                    {request.remarks && (
                      <div className="pt-1">
                        <span className="text-xs text-gray-400 font-bold uppercase block mb-1">
                          Remarks
                        </span>
                        <p className="text-xs text-gray-700 bg-gray-50 p-3 rounded-xl border border-gray-150 leading-relaxed">
                          {request.remarks}
                        </p>
                      </div>
                    )}

                    {request.reason && (
                      <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-700 text-xs">
                        <span className="font-bold block uppercase text-[10px] mb-1">
                          Rejection / Note Reason
                        </span>
                        <p>{request.reason}</p>
                      </div>
                    )}
                  </div>
                </RoundedContainer>
              </div>

              {/* Right: Lifecycle Progress Stepper (8 cols) */}
              <div className="lg:col-span-8">
                <RoundedContainer className="p-5 border border-gray-150 shadow-xs space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                    <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                      <ClockIcon className="w-5 h-5 text-blue-600" />
                      Lifecycle & Approval Progress
                    </h3>
                    <span className="text-xs text-gray-500">
                      Step-by-step audit & payout history
                    </span>
                  </div>
                  <FundRequestLifecycleTimeline lifecycle={request.lifecycle || []} />
                </RoundedContainer>
              </div>
            </div>

            {/* Linked Petty Cash Transactions Section (Full Width) */}
            <div className="space-y-4">
              {/* Filter Bar */}
              <RoundedContainer className="p-5 shadow-xs border border-gray-150 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Linked Petty Cash Transactions
                    </h3>
                    <p className="text-xs text-gray-500">
                      Filter and review all transactions recorded against this fund request
                    </p>
                  </div>
                  <span className="text-xs font-semibold px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
                    Total: {totalTxnCount} record{totalTxnCount !== 1 ? "s" : ""}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  {/* Search */}
                  <div className="md:col-span-4">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                      Search
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <SearchIcon className="h-5 w-5 text-gray-400" />
                      </span>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        placeholder="Search particulars..."
                        className="pl-10 pr-4 py-2 w-full rounded-xl border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm shadow-xs"
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <div className="md:col-span-3">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                      Status
                    </label>
                    <select
                      value={txnFilters?.approval_status || ""}
                      onChange={(e) => handleFilterChange("approval_status", e.target.value)}
                      className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-xs"
                    >
                      <option value="">All Statuses</option>
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                      <option value="REJECTED">Rejected</option>
                    </select>
                  </div>

                  {/* From Date */}
                  <div className="md:col-span-2.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                      From Date
                    </label>
                    <div className="[&_.react-datepicker-wrapper]:w-full">
                      <DateInput
                        label={null}
                        value={txnFilters?.from_date || ""}
                        onChange={(date) =>
                          handleFilterChange("from_date", date ? formatDateWithHyphens(date) : "")
                        }
                        className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-xs"
                        placeholderText="Select Date"
                        isClearable
                        selectsStart
                        startDate={txnFilters?.from_date ? new Date(txnFilters.from_date) : null}
                        endDate={txnFilters?.to_date ? new Date(txnFilters.to_date) : null}
                      />
                    </div>
                  </div>

                  {/* To Date */}
                  <div className="md:col-span-2.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                      To Date
                    </label>
                    <div className="[&_.react-datepicker-wrapper]:w-full">
                      <DateInput
                        label={null}
                        value={txnFilters?.to_date || ""}
                        onChange={(date) =>
                          handleFilterChange("to_date", date ? formatDateWithHyphens(date) : "")
                        }
                        className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-xs"
                        placeholderText="Select Date"
                        isClearable
                        selectsEnd
                        startDate={txnFilters?.from_date ? new Date(txnFilters.from_date) : null}
                        endDate={txnFilters?.to_date ? new Date(txnFilters.to_date) : null}
                        minDate={txnFilters?.from_date ? new Date(txnFilters.from_date) : null}
                      />
                    </div>
                  </div>
                </div>
              </RoundedContainer>

              {/* Transactions Table & Mobile Cards */}
              {transactionListing.isLoading ? (
                <div className="flex justify-center py-12 bg-white rounded-2xl border border-gray-150 shadow-xs">
                  <InlineLoader className="w-8 h-8 text-primary" />
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <RoundedContainer className="hidden md:block border border-gray-150 shadow-xs p-4">
                    <div className="max-w-full overflow-x-auto">
                      <Table className="table-auto w-full">
                        <TableHead>
                          <TableHeaderCell>#</TableHeaderCell>
                          <TableHeaderCell>Date</TableHeaderCell>
                          <TableHeaderCell>Particulars</TableHeaderCell>
                          <TableHeaderCell>Category</TableHeaderCell>
                          <TableHeaderCell>Type</TableHeaderCell>
                          <TableHeaderCell>Amount</TableHeaderCell>
                          <TableHeaderCell className="text-center">Status</TableHeaderCell>
                          <TableHeaderCell className="text-center">Actions</TableHeaderCell>
                        </TableHead>
                        <TableBody>
                          {transactions.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} className="text-center text-gray-400 py-8">
                                No transactions found matching your criteria.
                              </TableCell>
                            </TableRow>
                          ) : (
                            transactions.map((txn, idx) => (
                              <TableRow key={txn.transaction_id}>
                                <TableCell className="text-gray-500">
                                  {(txnFilters?.page_number || 0) * txnLimit + idx + 1}
                                </TableCell>
                                <TableCell className="text-gray-800 font-medium whitespace-nowrap">
                                  {txn.expenses_date}
                                </TableCell>
                                <TableCell className="text-gray-700">
                                  <div className="flex items-center gap-2">
                                    <span>{txn.particulars}</span>
                                    {txn.is_invoice_no_present === false && (
                                      <span
                                        title="Invoice missing"
                                        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-100 text-red-700 text-[10px] font-bold flex-shrink-0"
                                      >
                                        i
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-gray-600 font-medium">
                                  {txn.expenses_type_name || "-"}
                                </TableCell>
                                <TableCell>
                                  <span
                                    className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                                      txn.transaction_type === "CR"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-red-100 text-red-800"
                                    }`}
                                  >
                                    {txn.transaction_type || "DR"}
                                  </span>
                                </TableCell>
                                <TableCell className="font-bold text-gray-900">
                                  {txn.currency_symbol || txn.currency_type_symbol || "₹"}
                                  {txn.amount}
                                </TableCell>
                                <TableCell className="text-center">
                                  <span
                                    className={
                                      txn.approval_status === "APPROVED"
                                        ? "status-approved"
                                        : txn.approval_status === "PENDING"
                                        ? "status-pending"
                                        : "status-rejected"
                                    }
                                  >
                                    {txn.approval_status}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <button
                                    onClick={() => handleViewTxnDetails(txn.transaction_id)}
                                    className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                    title="View Details"
                                  >
                                    <EyeIcon className="w-5 h-5" />
                                  </button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </RoundedContainer>

                  {/* Mobile Card List View */}
                  <div className="md:hidden space-y-3">
                    {transactions.length === 0 ? (
                      <div className="text-center py-6 text-gray-400 bg-white rounded-2xl border border-gray-150">
                        No transactions found matching your criteria.
                      </div>
                    ) : (
                      transactions.map((txn) => (
                        <div
                          key={txn.transaction_id}
                          className="bg-white rounded-2xl border border-gray-150 p-4 shadow-xs space-y-3"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-2">
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  txn.transaction_type === "CR"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {txn.transaction_type}
                              </span>
                              <span className="text-xs text-gray-400 font-semibold">
                                {txn.expenses_date}
                              </span>
                            </div>
                            <span
                              className={
                                txn.approval_status === "APPROVED"
                                  ? "status-approved text-[10px] py-0.5 px-2"
                                  : txn.approval_status === "PENDING"
                                  ? "status-pending text-[10px] py-0.5 px-2"
                                  : "status-rejected text-[10px] py-0.5 px-2"
                              }
                            >
                              {txn.approval_status}
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-800 font-bold block">{txn.particulars}</span>
                            <span className="text-xs text-gray-500">
                              {txn.expenses_type_name || "General"}
                            </span>
                          </div>

                          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                            <span className="font-bold text-gray-900 text-base">
                              {txn.currency_symbol || txn.currency_type_symbol || "₹"}
                              {txn.amount}
                            </span>
                            <button
                              onClick={() => handleViewTxnDetails(txn.transaction_id)}
                              className="p-2 rounded-xl text-gray-500 bg-gray-50 border border-gray-200 active:scale-95"
                            >
                              <EyeIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Pagination */}
                  <RoundedContainer className="border border-gray-150 shadow-xs p-4">
                    <Pagination
                      currentPage={txnFilters?.page_number || 0}
                      lengthofItems={totalTxnCount}
                      limit={txnLimit}
                      onPageChange={handleTxnPageClick}
                    />
                  </RoundedContainer>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Transaction Details & Receipt View Modal */}
      <PettyCashDetailModal
        open={!!selectedTxnId}
        onClose={() => setSelectedTxnId(null)}
        transaction={activeTxn}
        isLoading={activeTxnQuery.isLoading}
      />

      {/* Fund Request Approval Modal */}
      <FundRequestApprovalModal
        open={approvalModal}
        onClose={() => setApprovalModal(false)}
        request={request}
        isApproving={isApproving}
        onApprove={handleApprove}
      />

      {/* Fund Request Mark As Paid Modal */}
      <FundRequestMarkAsPaidModal
        open={paidModal}
        onClose={() => setPaidModal(false)}
        request={request}
        isPaying={isMarkingAsPaid}
        onMarkAsPaid={(payload) => markAsPaid(payload)}
      />

      {/* Fund Request Edit Modal */}
      {formModal && (
        <FundRequestForm
          open={formModal}
          onClose={() => setFormModal(false)}
          editRequest={request}
          isUpdating={false}
          isSettingsLoading={isSettingsLoading}
          priorNoDays={priorNoDays}
          onSubmit={() => setFormModal(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        onDelete={() => deleteRequest({ request_id: requestId })}
        isLoading={isDeleting}
        message="Are you sure you want to delete this fund request?"
      />
    </div>
  );
}

export default PettyCashFundRequestDetail;
